import { useCallback, useMemo, useState } from "react";
import type {
  UpdateUserInfoRequest,
  UserDetail
} from "@dealer-center/ddc-pages-configuration-interface";

/**
 * The ten CC9-backed fields the User Information tab edits (FR-012, FR-014).
 *
 * A tuple of `UserDetail` keys rather than a free-form string union, so a typo cannot name a field
 * that does not exist and a renamed payload field breaks the build here rather than silently reading
 * `undefined`. `UserDetail` carries fifteen keys; the other five (`userId`, `platformId`, `parentId`,
 * `ownerId`, `created`) are identity and metadata, not user-editable copy.
 */
export const USER_INFO_FIELD_NAMES = [
  "title",
  "firstName",
  "lastName",
  "company",
  "email",
  "email2",
  "homePhone",
  "workPhone",
  "mobile",
  "fax"
] as const satisfies readonly (keyof UserDetail)[];

export type UserInfoFieldName = (typeof USER_INFO_FIELD_NAMES)[number];

export interface UserInfoEdits {
  /** The value to render for a field — the user's edit if there is one, else the stored value. */
  valueOf: (
    summary: UserDetail | undefined,
    field: UserInfoFieldName
  ) => string;
  setField: (field: UserInfoFieldName, value: string) => void;
  /** Whether any field currently differs from its stored value. */
  isDirty: (summary: UserDetail | undefined) => boolean;
  /**
   * Edited fields whose trimmed value differs from the trimmed stored one, sent trimmed. Both sides
   * are trimmed because `TextInput` fires `onChange` on blur, so tabbing through records an edit.
   */
  changedFields: (summary: UserDetail | undefined) => UpdateUserInfoRequest;
  /**
   * Discards every pending edit.
   *
   * **Added for User Manager, where the subject changes.** My Info never needs it — its subject is
   * fixed for the life of the page, so there is no moment at which edits stop applying. User Manager
   * must clear them when the operator moves to a different user (FR-023), because overrides keyed by
   * field name and not by user would otherwise bleed one person's typing onto another's record. That
   * is a data-integrity bug, not a cosmetic one: the fields would show values the operator never
   * entered for that user.
   *
   * It is **not** called on every selection change — FR-023 requires a prompt first, and the
   * selection proceeds only on an explicit choice to discard. See `usePendingEditsGuard`.
   */
  reset: () => void;
}

/**
 * Pending edits to the User Information form, held as **overrides on top of the fetched record**
 * rather than as a seeded copy of it.
 *
 * **This hook must be called above the tab panels, not inside `UserInformationTab`** (FR-009:
 * "Switching tabs MUST preserve pending edits on User Information, MUST NOT discard them"). `MyInfo`
 * hands the active tab's panel to Interstate and an empty fragment to every inactive one, so
 * `UserInformationTab` **unmounts** on each tab switch — state owned there would be silently
 * discarded the moment a user glanced at Login History, which is the specific thing FR-009 forbids.
 *
 * **Overrides, not a seeded draft**, for three reasons:
 *
 * 1. No seeding effect, so there is no window where the form renders empty before the record arrives
 *    and no question of what happens when react-query refetches underneath a populated form.
 * 2. FR-024's "reverting a field to its original value MUST clear its unsaved state" falls out of the
 *    comparison rather than needing its own bookkeeping — `isDirty` compares against the record, so
 *    typing a value back to what it was is not dirty.
 * 3. The hook needs no access to the query. `MyInfo` does not fetch `userinfo` (the tab does), and
 *    adding a second `useGetUserInfo` there just to seed state would couple the two.
 *
 * **Nothing in this hook saves.** `setField` updates local state and that is all. Persisting is the
 * page's job: User Manager sends `changedFields(summary)` through `useSaveUserInfo` and calls
 * `reset()` once the save lands for the subject still selected.
 *
 * `isDirty` is exported because FR-008a and FR-024 need it — and, **as of 2026-09-23, because
 * `HeaderActions` gates Save's `disabled` on it.** This paragraph previously said the opposite ("is
 * deliberately **not** wired to Save's `disabled` yet: a Save that enables itself when the form is
 * dirty would be a control that looks live and cannot act, which is what FR-052 forbids"). The
 * reviewer reversed that on the grounds that this build is not public, so a control reflecting its
 * true state is worth more during development than one held inert.
 *
 * ⚠️ **`isDirty` now has two consumers with different consequences** — the unsaved-changes guard and
 * Save's enabled state. `UserManager` derives it **once** and passes it to both, so they cannot
 * disagree about whether there are edits; do not re-derive it at either call site.
 */
export const useUserInfoEdits = (): UserInfoEdits => {
  const [edits, setEdits] = useState<
    Partial<Record<UserInfoFieldName, string>>
  >({});

  const valueOf = useCallback(
    (summary: UserDetail | undefined, field: UserInfoFieldName) =>
      edits[field] ?? summary?.[field] ?? "",
    [edits]
  );

  const setField = useCallback((field: UserInfoFieldName, value: string) => {
    setEdits(current => ({ ...current, [field]: value }));
  }, []);

  const isDirty = useCallback(
    (summary: UserDetail | undefined) =>
      USER_INFO_FIELD_NAMES.some(field => {
        const edited = edits[field];

        // An untouched field cannot be dirty, and `""` is a legitimate edit — hence `undefined`, not
        // falsiness. Clearing a populated field is a real change.
        return edited !== undefined && edited !== (summary?.[field] ?? "");
      }),
    [edits]
  );

  const changedFields = useCallback(
    (summary: UserDetail | undefined) =>
      USER_INFO_FIELD_NAMES.reduce<UpdateUserInfoRequest>((changed, field) => {
        const edited = edits[field];
        if (edited === undefined) {
          return changed;
        }

        const trimmed = edited.trim();

        return trimmed === (summary?.[field] ?? "").trim()
          ? changed
          : { ...changed, [field]: trimmed };
      }, {}),
    [edits]
  );

  const reset = useCallback(() => {
    setEdits({});
  }, []);

  return useMemo(
    () => ({ valueOf, setField, isDirty, changedFields, reset }),
    [valueOf, setField, isDirty, changedFields, reset]
  );
};
